import {DataTable, DataTableProps} from "../DataTable/DataTable";
import {PipelineFlowTransportEntity, Quantity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {useState} from "react";
import {FormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {Link} from "react-router-dom";
import {ActionIcon, Anchor, Group} from "@mantine/core";
import {usePagination} from "../DataTable/usePagination";
import {usePipelineFlowTransportDataTableFilter} from "./usePipelineFlowTransportDataTableFilter";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {DataTableSumFooter} from "../DataTable/footer/DataTableSumFooter/DataTableSumFooter";
import {FormattedCurrency} from "../../text/FormattedCurrency/FormattedCurrency";
import {IconReportMoney} from "@tabler/icons-react";
import {CostDetailsModal} from "../../modal/CostDetailsModal/CostDetailsModal";
import {useCostedDataTable} from "../DataTable/useCostedDataTable";
import {CostHeader} from "../DataTable/header/CostHeader/CostHeader";
import {DataTableCostCostSumFooter} from "../DataTable/footer/DataTableCostSumFooter/DataTableCostSumFooter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";

const EMPTY_ARRAY: PipelineFlowTransportEntity[] = []

export type PipelineFlowTransportDataTableProps =
  EntityDataTableProps<PipelineFlowTransportEntity>
  & {
  onEdit?: (record: PipelineFlowTransportEntity) => void
  onDelete?: (record: PipelineFlowTransportEntity) => void
}

export const PipelineFlowTransportDataTable = (props: PipelineFlowTransportDataTableProps) => {
  const {
    records: pipelineFlowTransports = EMPTY_ARRAY,
    recordsPerPageOptions,
    onEdit,
    onDelete,
    ...rest
  } = props;

  const [selectedRecord, setSelectedRecord] = useState<PipelineFlowTransportEntity | undefined>(undefined)

  const {t} = useTranslation(["common"]);

  const costedTableProps = useCostedDataTable({
    records: pipelineFlowTransports,
  })

  const {filteredRecords, filterConfig} = usePipelineFlowTransportDataTableFilter({
    records: pipelineFlowTransports
  })

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'dttm', direction: 'asc'}
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  return (
    <>
      <DataTable
        {...rest}
        {...(pagination ?? {})}
        sortStatus={sortStatus}
        onSortStatusChange={onSortStatusChange}
        columns={[
          {
            title: t("table.entity.pipelineflowtransport.column.date", "Date"),
            accessor: "dttm",
            width: 75,
            sortable: true,
            render: ({dttm}) => {
              return <FormattedDateTime date={dttm} displayTime={false}/>
            },
          },
          {
            title: t("table.entity.pipelineflowtransport.column.timegroup", "Time Group"),
            accessor: "timeGroup",
            width: 100,
            sortable: true,
            render: ({timeGroup}) => (
              t(timeGroup.displayValue.id, timeGroup.displayValue.defaultMessage)
            ),
          },
          {
            title: t("table.entity.pipelineflowtransport.column.pipeline", "Pipeline"),
            accessor: "pipelineId",
            width: 100,
            sortable: true,
            render: ({pipelineId, pipelineName}) => {
              if (pipelineId) {
                return (
                  <Anchor component={Link} to={`/pipelines/profile?id=${pipelineId}`}>
                    <DefaultText value={pipelineName}/>
                  </Anchor>
                )
              } else {
                return (
                  <DefaultText value={pipelineName}/>
                )
              }
            },
          },
          {
            ...getColumnFilterProps(filterConfig, 'sourceObject'),
            title: t("table.entity.pipelineflowtransport.column.source", "Source"),
            accessor: "source",
            width: 100,
            sortable: true,
          },
          {
            ...getColumnFilterProps(filterConfig, 'destObject'),
            title: t("table.entity.pipelineflowtransport.column.destination", "Destination"),
            accessor: "destination",
            width: 100,
            sortable: true,
          },
          {
            title: t("table.entity.pipelineflowtransport.column.freighttype", "Freight Type"),
            accessor: "freightTypeName",
            width: 100,
            sortable: true,
            render: ({freightTypeName}) => (
              <DefaultText value={freightTypeName}/>
            )
          },
          {
            title: t("table.entity.pipelineflowtransport.column.servicetype", "Service Type"),
            accessor: "serviceTypeName",
            width: 100,
            sortable: true,
            render: ({serviceTypeId, serviceTypeName}) => (
              serviceTypeId ? <DefaultText value={serviceTypeName}/> : undefined
            )
          },
          {
            title: t("table.entity.pipelineflowtransport.column.allocation", "Allocation"),
            accessor: "allocationType",
            width: 100,
            sortable: true,
            render: ({allocationType}) => (
              t(allocationType.displayValue.id, allocationType.displayValue.defaultMessage)
            )
          },
          {
            title: t("table.entity.pipelineflowtransport.column.quantity", "Qty"),
            accessor: "grossQuantity",
            width: 100,
            sortable: true,
            render: ({grossQuantity, unitOfMeasure}) => {
              const qty: Partial<Quantity> = {
                number: grossQuantity,
                units: unitOfMeasure
              }

              return <FormattedQuantity value={qty}/>
            },
            footer: (
              <DataTableSumFooter
                value={{
                  number: filteredRecords.reduce((prev, curr) => prev + curr.grossQuantity, 0)
                }}
              />
            ),
          },
          {
            title: (
              <CostHeader
                {...costedTableProps}
                defaultTitle={t("table.entity.pipelineflowtransport.column.cost", "Cost")}/>
            ),
            accessor: "totalCost",
            width: 125,
            sortable: true,
            render: (record) => {
              return (
                <Group spacing={4} position="right" noWrap>
                  <FormattedCurrency value={record.getTotalCost(costedTableProps.selectedRateModel)}/>
                  <ActionIcon variant="transparent" onClick={() => setSelectedRecord(record)}>
                    <IconReportMoney size={14}/>
                  </ActionIcon>
                </Group>
              )
            },
            footer: (
              <DataTableCostCostSumFooter
                value={filteredRecords.reduce((prev, curr) => prev + (curr.getTotalCost(costedTableProps.selectedRateModel) ?? 0), 0)}
              />
            ),
          },
        ]}
      />
      {selectedRecord &&
        <CostDetailsModal
          title={"Cost Breakdown"}
          size="xl"
          opened={true}
          onClose={() => setSelectedRecord(undefined)}
          value={selectedRecord}/>
      }
    </>
  );
};
